<script setup lang="ts">
import { computed } from "vue"
import { ButtonSize, TuIconButton, TuTable, TuTableHeader, TuTableRow } from "@tinyurldev/tu-ui-vue"
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core"

import type { Domain } from "@Modules/urls/Data/Models/DomainModel"
import useTranslation from "@Composables/useTranslation"
import { toLongDate } from "@Resources/js/helpers/support"

//
// Setup
const { __ } = useTranslation()
const breakpoints = useBreakpoints(breakpointsBootstrapV5)
const isMobileView = breakpoints.smallerOrEqual("xl")

//
// Props
interface Props {
  domains?: Domain[]
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  domains: () => [],
  loading: true,
});

//
// Emit
interface Events {
  (e: "action", domain: Domain): void;
}

const emit = defineEmits<Events>();

//
// Computed
const sortedDomains = computed<Domain[]>(() => {
  return [...props.domains].sort((a, b) => {
    const domainA = a.domain.toLowerCase()
    const domainB = b.domain.toLowerCase()
    return domainA < domainB ? -1 : domainA > domainB ? 1 : 0
  });
});

function getDomainStatuses(domain: Domain): string[] {
  const statuses: string[] = [];

  if (domain.isBlocked) statuses.push(__("organization_domains_settings.status_blocked"));
  if (domain.isDeleted) statuses.push(__("organization_domains_settings.status_deleted"));
  if (domain.isDisabled) statuses.push(__("organization_domains_settings.status_disabled"));
  if (!domain.isActive) statuses.push(__("organization_domains_settings.status_inactive"));
  if (domain.isPurchased) statuses.push(__("organization_domains_settings.status_purchased"));
  if (statuses.length === 0) statuses.push(__("organization_domains_settings.status_active"));
  if (!domain.hasSslCertificate) statuses.push(__("organization_domains_settings.status_ssl_pending"));

  return statuses;
}
</script>

<template>
  <TuTable class="org-domains-table _table">
    <template
      v-if="!isMobileView"
      #header
    >
      <TuTableHeader>
        <th class="org-domains-table__name-cell">
          {{ __('organization_domains_settings.table_header_domain') }}
        </th>
        <th>{{ __('organization_domains_settings.table_header_status') }}</th>
        <th>{{ __('organization_domains_settings.table_header_date_added') }}</th>
        <th>{{ __('organization_domains_settings.table_header_expiration_date') }}</th>
        <th class="org-domains-table__action-cell">
          {{ __('organization_domains_settings.table_header_action') }}
        </th>
      </TuTableHeader>
    </template>

    <TuTableRow
      v-if="loading"
      class="_loading"
    >
      <td
        class="text-center"
        colspan="5"
      >
        <i class="fa fa-spinner fa-spin" />
        {{ __('organization_domains_settings.table_loading') }}
      </td>
    </TuTableRow>

    <TuTableRow v-else-if="!sortedDomains.length">
      <td
        class="_empty text-center"
        colspan="5"
      >
        <i class="fa-solid fa-circle-exclamation" />
        {{ __('organization_domains_settings.table_empty') }}
      </td>
    </TuTableRow>

    <template v-else-if="!isMobileView">
      <TuTableRow
        v-for="domain in sortedDomains"
        :key="domain.domain"
        class="org-domains-table__row _row"
        :class="{ 'org-domains-table__row--disabled': domain.isDeleted || domain.isBlocked }"
      >
        <td class="org-domains-table__name-cell _name">
          {{ domain.domain }}
        </td>
        <td class="_status">
          <p
            v-for="status in getDomainStatuses(domain)"
            :key="status"
            class="org-domains-table__status-badge"
          >
            {{ status }}
          </p>
        </td>
        <td class="_date">
          <template v-if="domain.created_at">
            {{ toLongDate(domain.created_at) }}
          </template>
          <template v-else>
            {{ "—" }}
          </template>
        </td>
        <td class="_expiration">
          <template v-if="domain.renew_at ">
            <p class="org-domains-table__expiration-content">
              {{ toLongDate(domain.renew_at ) }}
            </p>
            <p
              class="org-domains-table__expiration-tag "
              :class="{ 'org-domains-table__expiration-tag--off': !domain.isRenewalEnabled }"
            >
              {{ __('organization_domains_settings.auto_renewal', { status: domain.isRenewalEnabled ? __('organization_domains_settings.auto_renewal_on') : __('organization_domains_settings.auto_renewal_off') }) }}
            </p>
          </template>
          <template v-else>
            {{ "—" }}
          </template>
        </td>
        <td class="org-domains-table__action-cell">
          <TuIconButton
            class="_action"
            :aria-label="__('organization_domains_settings.domain_actions')"
            icon-class="fa-solid fa-gear"
            is-outlined
            :size="ButtonSize.MEDIUM"
            :is-disabled="domain.isBlocked"
            @click="emit('action', domain)"
          />
        </td>
      </TuTableRow>
    </template>

    <template v-else>
      <TuTableRow
        v-for="domain in sortedDomains"
        :key="domain.domain"
        class="_row"
      >
        <td colspan="4">
          <div class="org-domains-table__cell-mobile org-domains-table__cell-mobile--full">
            <p class="org-domains-table__cell-mobile-content _name">
              {{ domain.domain }}
            </p>
          </div>
          <div class="org-domains-table__cell-mobile">
            <p class="org-domains-table__cell-mobile-title">
              {{ __('organization_domains_settings.table_header_status') }}
            </p>
            <div class="org-domains-table__cell-mobile-content _status">
              <p
                v-for="status in getDomainStatuses(domain)"
                :key="status"
                class="org-domains-table__status-badge"
              >
                {{ status }}
              </p>
            </div>
          </div>
          <div class="org-domains-table__cell-mobile">
            <p class="org-domains-table__cell-mobile-title">
              {{ __('organization_domains_settings.table_header_date_added') }}
            </p>
            <p class="org-domains-table__cell-mobile-content _date">
              <template v-if="domain.created_at">
                {{ toLongDate(domain.created_at) }}
              </template>
              <template v-else>
                {{ "—" }}
              </template>
            </p>
          </div>
          <div class="org-domains-table__cell-mobile">
            <p class="org-domains-table__cell-mobile-title">
              {{ __('organization_domains_settings.table_header_expiration_date') }}
            </p>
            <div>
              <template v-if="domain.renew_at ">
                <p class="org-domains-table__expiration-content">
                  {{ toLongDate(domain.renew_at ) }}
                </p>
                <p
                  class="org-domains-table__expiration-tag "
                  :class="{ 'org-domains-table__expiration-tag--off': !domain.isRenewalEnabled }"
                >
                  {{ __('organization_domains_settings.auto_renewal', { status: domain.isRenewalEnabled ? __('organization_domains_settings.auto_renewal_on') : __('organization_domains_settings.auto_renewal_off') }) }}
                </p>
              </template>
              <template v-else>
                {{ "—" }}
              </template>
            </div>
          </div>
          <div class="org-domains-table__cell-mobile">
            <p class="org-domains-table__cell-mobile-title">
              {{ __('organization_domains_settings.table_header_action') }}
            </p>
            <TuIconButton
              class="org-domains-table__action-cell-mobile _action"
              icon-class="fa-solid fa-gear"
              is-outlined
              :size="ButtonSize.MEDIUM"
              :is-disabled="domain.isBlocked"
              @click="emit('action', domain)"
            />
          </div>
        </td>
      </TuTableRow>
    </template>
  </TuTable>
</template>

<style lang="scss">
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as colors;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-domains-table {
  table tbody {
    max-height: 500px;
  }

  &__row {
    td {
      font-size: typography.$s-font-size;
      font-weight: typography.$font-weight-medium;
      line-height: typography.$s-line-height;
    }

    &--disabled {
      td {
        color: colors.$disabled;
      }
    }
  }

  &__name-cell {
    width: 50%;
  }

  &__expiration-tag {
    color: colors.$secondary;
    font-size: typography.$xxs-font-size;
    line-height: typography.$xxs-line-height;

    &--off {
      color: colors.$alert;
    }
  }

  &__action-cell {
    width: 10%;
    white-space: nowrap;
  }

  &__cell-mobile {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: spacing.$xxxs;

    &:is(:first-child) {
      margin-top: spacing.$xxxs;
    }

    &:is(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &--full {
      grid-template-columns: 1fr;
    }
  }

  &__cell-mobile-title {
    font-weight: typography.$font-weight-bold;
    margin: 0;
  }

  &__action-cell-mobile {
    justify-self: flex-start;
  }

  &__status-badge,
  &__expiration-content,
  &__expiration-tag,
  &__cell-mobile-content {
    margin: 0;
  }
}
</style>
